import {
  FormControl,
  FormHelperText,
  FormLabel,
  InputProps,
  Skeleton,
} from '@mui/joy'
import { ParseKeys } from 'i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import type { InputMaskElement, Definitions } from 'imask'
import React, { ChangeEvent, forwardRef } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IMaskInput } from 'react-imask'
import { useParserI18nMessage } from '~/shared/lib/useParserI18nMessage'
import { Input } from '../ui/styled'

type MaskInputProps = {
  label?: ParseKeys
  placeholder?: ParseKeys
  name: string
  skeletonShow?: boolean
  mask?: string
  definitions?: Definitions
} & Omit<InputProps, 'name' | 'slotProps'>

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: MaskInputProps['name']
  params: {
    mask?: MaskInputProps['mask']
    definitions?: MaskInputProps['definitions']
  }
}

const MaskAdapter = forwardRef<HTMLElement, CustomProps>(function Wrapper(
  { onChange, params, ...other },
  ref: InputMaskElement,
) {
  return (
    <IMaskInput
      {...other}
      mask={params.mask || ''}
      definitions={
        params.definitions || {
          '#': /[0-9]/,
        }
      }
      onInput={(value: ChangeEvent<HTMLInputElement>) => onChange(value)}
      inputRef={ref}
    />
  )
})

export function MaskInput({
  label,
  placeholder,
  name,
  skeletonShow,
  mask,
  definitions,
  ...inputProps
}: MaskInputProps) {
  const { t } = useTranslation()
  const { parserI18nMessage } = useParserI18nMessage()
  return (
    <Controller
      name={name}
      render={({ field, fieldState: { error, invalid } }) => (
        <FormControl error={invalid}>
          {label && <FormLabel>{t(label)}</FormLabel>}

          <Input
            fullWidth
            size='lg'
            {...inputProps}
            {...field}
            {...(placeholder ? { placeholder: t(placeholder) } : {})}
            error={invalid}
            slotProps={{
              ...(skeletonShow ? { root: { component: Skeleton } } : {}),
              input: {
                component: MaskAdapter,
                params: {
                  mask,
                  definitions,
                },
              },
            }}
          />

          {invalid && (
            <FormHelperText data-testid={`error-message-${label && t(label)}`}>
              {parserI18nMessage(error?.message)}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  )
}
